import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  idParam,
  PARAM,
  parsePage,
  parseSegment,
  textParam,
  type ExtraFilter,
  type StatusSegment,
  type UiFilters,
} from './url-filters';

// Filtros de la Lista Maestra sincronizados con la URL (deep-linking, UX-NAV-02).
// El estado de la vista vive en searchParams; recargar/compartir reproduce la vista.
// Los tipos y la traducción pura viven en `url-filters` y se reexportan aquí.

export {
  isFiltered,
  parsePage,
  parseSegment,
  toQueryParams,
  type ExtraFilter,
  type QueryParams,
  type StatusSegment,
  type UiFilters,
} from './url-filters';

/** Parámetros de la URL que son filtros de la Lista Maestra (más la página). */
const FILTER_PARAMS = ['estado', 'q', ...Object.values(PARAM), 'pagina'];

export interface UseUrlFilters {
  filters: UiFilters;
  setSegment: (s: StatusSegment) => void;
  setQuery: (q: string) => void;
  /** Aplica filtros de "+ Filtro"; cambiar la norma sin cláusula quita la cláusula. */
  setFilters: (next: Partial<Record<ExtraFilter, string | null>>) => void;
  /** Quita un filtro; quitar la norma quita también la cláusula. */
  clearFilter: (key: ExtraFilter) => void;
  setPage: (page: number) => void;
  /** Quita todos los filtros y vuelve a la primera página. */
  clearAll: () => void;
}

export function useUrlFilters(): UseUrlFilters {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<UiFilters>(
    () => ({
      segment: parseSegment(params.get('estado')),
      q: params.get('q') ?? '',
      norm: textParam(params.get('norma')),
      clause: textParam(params.get('norma')) ? textParam(params.get('clausula')) : null,
      type: idParam(params.get('tipo')),
      area: idParam(params.get('proceso')),
      page: parsePage(params.get('pagina')),
    }),
    [params],
  );

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value === null || value === '') {
              p.delete(key);
            } else {
              p.set(key, value);
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    filters,
    // Cambiar un filtro reinicia la paginación (vuelve a la primera página).
    setSegment: (s) => patch({ estado: s === 'todos' ? null : s, pagina: null }),
    setQuery: (q) => patch({ q, pagina: null }),
    setFilters: (next) => {
      const change: Record<string, string | null> = { pagina: null };
      for (const [key, value] of Object.entries(next) as [ExtraFilter, string | null][]) {
        change[PARAM[key]] = value;
      }
      if ('norm' in next && !('clause' in next)) {
        change[PARAM.clause] = null;
      }
      patch(change);
    },
    clearFilter: (key) =>
      patch({
        [PARAM[key]]: null,
        ...(key === 'norm' ? { [PARAM.clause]: null } : {}),
        pagina: null,
      }),
    setPage: (page) => patch({ pagina: page <= 1 ? null : String(page) }),
    clearAll: () => patch(Object.fromEntries(FILTER_PARAMS.map((k) => [k, null]))),
  };
}
